import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { PdBaseUIInput } from "../base/pd-base-ui-input.js";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-checkbox/PdCheckbox.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Checkbox, radio, or switch input component.
*
* @tagname pd-checkbox
* @summary Checkbox, radio button, switch toggle, or selectable card input.
*
* @event pd-form-element-change - Fired when value changes. Detail: `{ value, name, parsedValue, state }`.
* @event enter-pressed - Fired when Enter key is pressed.
*
* @slot - Label text for the checkbox, or card content when checkType="CARD".
*
* @cssprop --pd-cb-col - Checked icon color.
* @cssprop --pd-cb-unset-col - Unchecked icon color (e.g. the switch/checkbox ✕). Default: neutral gray `var(--pd-default-disabled-dark-col)`.
* @cssprop --pd-cb-bg-col - Checked background color.
* @cssprop --pd-cb-unset-bg-col - Unchecked background color.
* @cssprop --pd-cb-border-col - Border color.
* @cssprop --pd-cb-border-col-readonly - Readonly border color.
* @cssprop --pd-cb-border-radius - Border radius.
* @cssprop --pd-cb-size - Size of the check box (icon). Default: `1.3rem`.
* @cssprop --pd-cb-border-width - Border width of the check box. Default: `2px`.
* @cssprop --pd-cb-font-col - Label text color.
* @cssprop --pd-cb-font-col-readonly - Readonly label color.
* @cssprop --pd-cb-font-col-disabled - Disabled label color.
* @cssprop --pd-cb-switch-height - Switch variant height.
* @cssprop --pd-cb-switch-paddle-col - Switch paddle background.
* @cssprop --pd-cb-card-padding - Card content padding. Default: `0.75rem`.
*/
var PdCheckbox = class extends PdBaseUIInput {
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        display: inline-block;

        /* active checkbox */
        --pd-icon-bg-col-active: var(
          --pd-cb-bg-col,
          var(--pd-default-lightest-col)
        );
        --pd-icon-col-active: var(
          --pd-cb-col,
          var(--pd-default-success-dark-col)
        );

        /* inactive checkbox */
        --pd-icon-bg-col: var(
          --pd-cb-unset-bg-col,
          var(--pd-default-lightest-col)
        );
        --pd-icon-col: var(
          --pd-cb-unset-col,
          var(--pd-default-disabled-dark-col)
        );

        --pd-icon-bg-col-hover: var(
          --pd-cb-bg-col,
          var(--pd-default-light-col)
        );
      }

      :host([checkType="SWITCH"]) {
        /* derzeit keine Angaben */
      }

      .div-container {
        display: flex;
        align-items: center;
        cursor: pointer;
      }

      .div-container.disabled {
        opacity: 0.7;
        cursor: auto;
      }

      .div-container.readonly {
        cursor: auto;
      }

      .label {
        margin-left: 0.1rem;
        color: var(--pd-cb-font-col, var(--pd-default-font-input-col));
        font-family: var(--pd-default-font-input-family);
        text-align: left;
        font-size: var(--pd-default-font-input-size);
      }

      .readonly .label {
        color: var(
          --pd-cb-font-col-readonly,
          var(--pd-default-disabled-text-col)
        );
      }

      .disabled .label {
        color: var(
          --pd-cb-font-col-disabled,
          var(--pd-default-disabled-text-col)
        );
      }

      /*
       * Class checkbox for icon and a element (checkbox case)
       */
      .checkbox {
        display: flex;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
        line-height: 0;
        align-items: center;
        justify-content: center;
        margin-right: 0.2rem;
        margin-bottom: 0.2rem;
      }

      .checkbox:focus {
        outline: none;
      }

      .checkbox:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-sm));
      }

      .checkbox pd-icon {
        --pd-icon-size: var(--pd-cb-size, 1.3rem);
        border: var(--pd-cb-border-width, 2px) solid
          var(--pd-cb-border-col, var(--pd-default-col));
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-sm));
      }

      .readonly .checkbox pd-icon,
      .disabled .checkbox pd-icon {
        border-color: var(
          --pd-cb-border-col-readonly,
          var(--pd-default-disabled-col)
        );
      }

      /*
       * Class Radio for icon and a element (radio group case)
       */
      .radio-outer .label {
        margin-left: 0.7rem;
      }

      .radio {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .radio:focus {
        outline: none;
      }

      .radio:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: 50%;
      }

      .radio pd-icon {
        --pd-icon-size: 1.5rem;
        --pd-icon-round-padding: 0.1rem;
        border: 2px solid var(--pd-cb-border-col, var(--pd-default-col));
      }

      .readonly .radio pd-icon,
      .disabled .radio pd-icon {
        border-color: var(
          --pd-cb-border-col-readonly,
          var(--pd-default-disabled-col)
        );
      }

      /* Card styles – selectable content block without indicator */
      .card-outer {
        display: block;
      }

      .card-outer .label {
        margin-left: 0;
      }

      .card {
        border: 3px solid transparent;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
        padding: var(--pd-cb-card-padding, 0.75rem);
        cursor: pointer;
        transition:
          border-color 0.15s ease,
          box-shadow 0.15s ease;
      }

      .card:focus {
        outline: none;
      }

      .card:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
      }

      .card.isUnchecked:hover {
        border-color: var(--pd-default-light-col);
      }

      .card.isChecked {
        border-color: var(--pd-cb-border-col, var(--pd-default-col));
        box-shadow: 0 0 0 2px var(--pd-default-lighter-col);
      }

      .disabled .card,
      .readonly .card {
        cursor: auto;
      }

      .disabled .card.isUnchecked:hover {
        border-color: transparent;
      }

      /* Switch styles */
      .switch {
        height: var(--pd-cb-switch-height);
        position: relative;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }

      .switch-paddle:focus {
        outline: none;
      }

      .switch-paddle:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
      }

      .switch .label {
        margin-left: 0.5rem;
      }

      .switch-paddle {
        display: inline-block;
        vertical-align: baseline;
        margin: 0;
        position: relative;
        min-width: 4.1rem;
        max-width: 4.1rem;
        height: 1.9rem;
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
        background: var(--pd-cb-switch-paddle-col, var(--pd-default-col));
        cursor: pointer;
      }

      .switch-paddle pd-icon {
        --pd-icon-size: 1.25rem;
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        border: 2px solid var(--pd-cb-border-col, var(--pd-default-col));
        box-shadow: var(--pd-shadow-sm);
        border-radius: var(--pd-cb-border-radius, var(--pd-radius-md));
        -webkit-transition: all 0.25s ease-out;
        transition: all 0.25s ease-out;
        content: "";
      }

      .disabled .switch-paddle {
        cursor: auto;
      }

      .readonly .switch-paddle {
        cursor: auto;
        max-width: 2rem;
        min-width: 2rem;
        background: none;

        /* Prüfen TODO
        --pd-icon-bg: transparent;
        --pd-icon-fill: var(--card-dark-red);
        --pd-icon-fill-hover: var(--pd-icon-fill);
        --pd-icon-fill-active: var(--card-medium-green);
        */
      }

      .readonly .switch-paddle pd-icon {
        border-color: var(--pd-cb-border-col-readonly, transparent);
      }

      .readonly .isChecked.switch-paddle pd-icon {
        transform: translate3d(0, 0, 0);
        left: 0rem;
      }

      .isChecked.switch-paddle pd-icon {
        left: 2.25rem;
      }

      @media (min-width: 580px) {
        :host {
          font-size: 1rem;
        }
      }
    `];
	}
	constructor() {
		super();
		this.checkType = "CHECK";
		this._inputType = 3;
	}
	setCheckedState(newValue) {
		this._value = newValue;
	}
	render() {
		const checked = this._value === "true";
		const isCard = this.checkType === "CARD";
		const inputId = `${this.id}Check`;
		const classMapVal = {
			disabled: this.disabled,
			switch: this.checkType === "SWITCH",
			"radio-outer": this.checkType === "RADIO",
			"card-outer": isCard,
			readonly: this.readonly,
			"div-container": true
		};
		const aClassMap = {
			"switch-paddle": this.checkType === "SWITCH",
			checkbox: this.checkType === "CHECK",
			radio: this.checkType === "RADIO",
			card: isCard,
			isChecked: checked,
			isUnchecked: !checked
		};
		const role = this.checkType === "RADIO" || isCard ? "radio" : this.checkType === "SWITCH" ? "switch" : "checkbox";
		if (isCard) return html`
        ${this._renderLabel(inputId)}
        <div @click="${this._onClick}" class="${classMap(classMapVal)}">
          <div
            class="${classMap(aClassMap)}"
            tabindex="${this.disabled ? -1 : 0}"
            @keydown="${this._onKeyDown}"
            role="${role}"
            aria-checked="${checked}"
            aria-disabled="${this.disabled}"
            aria-readonly="${this.readonly}"
          >
            <span class="label"><slot></slot></span>
          </div>
        </div>
        ${this._renderErrorMsg()}
      `;
		return html`
      ${this._renderLabel(inputId)}
      <div @click="${this._onClick}" class="${classMap(classMapVal)}">
        <div
          class="${classMap(aClassMap)}"
          tabindex="${this.disabled ? -1 : 0}"
          @keydown="${this._onKeyDown}"
          role="${role}"
          aria-checked="${checked}"
          aria-disabled="${this.disabled}"
          aria-readonly="${this.readonly}"
        >
          <pd-icon
            icon="${this.checkType === "SWITCH" ? pdIcons.ICON_CHECKBOX : this.checkType === "RADIO" ? pdIcons.ICON_CIRCLE : pdIcons.ICON_CHECKBOX_ONLY_CHECK}"
            class="${this.checkType === "RADIO" ? "round" : ""}"
            ?activeIcon="${checked}"
          ></pd-icon>
        </div>
        <span class="label"><slot></slot></span>
      </div>
      ${this._renderErrorMsg()}
    `;
	}
	_onClick(event) {
		if (this.disabled || this.readonly) return;
		const checked = this._value === "true";
		this._handleChangedValue(!checked ? "true" : "false", event, true);
	}
	_onKeyDown(event) {
		if (this.disabled || this.readonly) return;
		if (event.key === " " || event.code === "Space") {
			event.preventDefault();
			this._onClick(event);
		} else if (event.key === "Enter") {
			event.preventDefault();
			this.dispatchEvent(new CustomEvent("enter-pressed", {
				detail: {
					value: this._value,
					name: this.valueName || this.id || "field"
				},
				bubbles: true,
				composed: true
			}));
		}
	}
	_getInitialValue(reset) {
		return reset ? this.initValue || "false" : this.initValue || this._value || "false";
	}
	clear() {
		this.reset();
	}
	_getParsedValue() {
		return (/* @__PURE__ */ new Set([
			true,
			"true",
			"on",
			"1",
			1
		])).has(this._value);
	}
};
__decorate([property({
	type: String,
	reflect: true
})], PdCheckbox.prototype, "checkType", void 0);
//#endregion
export { PdCheckbox };
